// Type aliases for cleaner imports of generated employee-related types
export type {
  models_Employee as Employee,
  models_EmployeeQualification as EmployeeQualification,
} from '@/api';

// Import for use in type alias below
import type { models_Employee } from '@/api';

export {
  models_EntityStatus as EntityStatus,
  models_GenderType as GenderType,
  models_QualificationLevel as QualificationLevel,
  models_ProfessionalRole as ProfessionalRole,
} from '@/api';

// Re-export request/response types with cleaner names
export type {
  employees_CreateEmployeeRequest as CreateEmployeeRequest,
  employees_UpdateEmployeeRequest as UpdateEmployeeRequest,
  employees_CreateEmployeeQualificationRequest as CreateEmployeeQualificationRequest,
  employees_UpdateEmployeeQualificationRequest as UpdateEmployeeQualificationRequest,
  employees_EmployeeAuditRecord as EmployeeAuditRecord,
  employees_EmployeeAuditHistoryResponse as EmployeeAuditHistoryResponse,
  // Login provisioning (issue #354)
  employees_ProvisionLoginRequest as ProvisionLoginRequest,
  employees_ProvisionLoginResponse as ProvisionLoginResponse,
  employees_LinkLoginRequest as LinkLoginRequest,
  employees_LinkLoginResponse as LinkLoginResponse,
  employees_ReissueLoginResponse as ReissueLoginResponse,
  employees_LoginStatusResponse as LoginStatusResponse,
  // Revoke login (issue #1255)
  employees_RevokeLoginResponse as RevokeLoginResponse,
  // Slim any-module employee picker (issue #1688)
  employees_EmployeePickerEntry as EmployeePickerEntry,
  employees_EmployeePickerResponse as EmployeePickerResponse,
  // Allergies and free-text emergency contacts (migration 179)
  models_EmployeeAllergy as EmployeeAllergy,
  models_EmployeeEmergencyContact as EmployeeEmergencyContact,
  employees_EmployeeAllergyRequest as EmployeeAllergyRequest,
  employees_EmployeeEmergencyContactRequest as EmployeeEmergencyContactRequest,
} from '@/api';



// Import the enums first
import {
  models_EntityStatus,
  models_GenderType,
  models_QualificationLevel,
  models_ProfessionalRole,
} from '@/api';

// For convenience, also export the enum values with shorter names
export { models_EntityStatus };
export { models_GenderType };
export { models_QualificationLevel };
export { models_ProfessionalRole };

// Also export models_Employee alias for compatibility
export type { models_Employee };

import type { I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import type { CustomEnum } from '@/types/organisation';

/**
 * Translated labels for every {@link models_ProfessionalRole}, reusing the
 * existing `employees.enums.role.*` catalog ids so analytics dropdowns can offer
 * the full static enum (not a page-scraped subset) with the same translations
 * the employee form already ships.
 */
export const getProfessionalRoleLabels = (
  i18n: I18n,
): Record<string, string> => ({
  [models_ProfessionalRole.ProfessionalRoleIntern]: i18n._(t`employees.enums.role.intern`),
  [models_ProfessionalRole.ProfessionalRolePedagogicAssistent]: i18n._(t`employees.enums.role.pedagogicAssistent`),
  [models_ProfessionalRole.ProfessionalRoleApprentice]: i18n._(t`employees.enums.role.apprentice`),
  [models_ProfessionalRole.ProfessionalRoleApprenticeShort]: i18n._(t`employees.enums.role.apprenticeShort`),
  [models_ProfessionalRole.ProfessionalRoleEducator]: i18n._(t`employees.enums.role.educator`),
  [models_ProfessionalRole.ProfessionalRoleGroupHead]: i18n._(t`employees.enums.role.groupHead`),
  [models_ProfessionalRole.ProfessionalRoleMainGroupHead]: i18n._(t`employees.enums.role.mainGroupHead`),
  [models_ProfessionalRole.ProfessionalRoleDaycareCenterHead]: i18n._(t`employees.enums.role.daycareCenterHead`),
  [models_ProfessionalRole.ProfessionalRoleCleaningStaff]: i18n._(t`employees.enums.role.cleaningStaff`),
  [models_ProfessionalRole.ProfessionalRoleJanitor]: i18n._(t`employees.enums.role.janitor`),
  [models_ProfessionalRole.ProfessionalRoleChef]: i18n._(t`employees.enums.role.chef`),
  [models_ProfessionalRole.ProfessionalRoleGoverness]: i18n._(t`employees.enums.role.governess`),
  [models_ProfessionalRole.ProfessionalRoleManagingDirector]: i18n._(t`employees.enums.role.managingDirector`),
  [models_ProfessionalRole.ProfessionalRoleConsultant]: i18n._(t`employees.enums.role.consultant`),
  [models_ProfessionalRole.ProfessionalRoleOther]: i18n._(t`employees.enums.role.other`),
});

/**
 * The English default label of every system professional role, as seeded by
 * `db/migrations/046_employee_role_hierarchy_qualification.up.sql`
 * (`'employees', 'role'` rows). A system role whose `label_default` differs
 * from it carries an org-level rename (`PATCH /access-roles/:value`); an
 * override row that only deactivates keeps the English default.
 */
export const SYSTEM_ROLE_DEFAULT_LABELS: Readonly<Record<string, string>> = {
  INTERN: 'Intern',
  PEDAGOGIC_ASSISTENT: 'Pedagogic Assistant',
  APPRENTICE: 'Apprentice',
  APPRENTICE_SHORT: 'Apprentice (Short)',
  EDUCATOR: 'Educator',
  GROUP_HEAD: 'Group Head',
  MAIN_GROUP_HEAD: 'Main Group Head',
  DAYCARE_CENTER_HEAD: 'Daycare Center Head',
  CLEANING_STAFF: 'Cleaning Staff',
  JANITOR: 'Janitor',
  CHEF: 'Chef',
  GOVERNESS: 'Governess',
  MANAGING_DIRECTOR: 'Managing Director',
  CONSULTANT: 'Consultant',
  OTHER: 'Other',
};

/**
 * Resolves the display label of a professional role (issue #1668, D3).
 * System value renamed at org level (#1670) -> the row's
 * label_translations[i18n.locale] || label_default;
 * other system value -> the static translated label (getProfessionalRoleLabels);
 * custom value -> label_translations[i18n.locale] || label_default of its
 * effective employees.role enum row (active or not);
 * unknown -> the raw value. Empty/nullish -> ''.
 */
export const resolveProfessionalRoleLabel = (
  value: string | null | undefined,
  i18n: I18n,
  enums: readonly CustomEnum[],
): string => {
  if (!value) return '';
  const systemLabels = getProfessionalRoleLabels(i18n);
  const row = enums.find((e) => e.enum_field === 'role' && e.enum_value === value);
  if (Object.prototype.hasOwnProperty.call(systemLabels, value)) {
    const renamed = !!row?.label_default && row.label_default !== SYSTEM_ROLE_DEFAULT_LABELS[value];
    if (!renamed) return systemLabels[value];
  }
  if (!row) return value;
  return row.label_translations?.[i18n.locale] || row.label_default || value;
};

/** Translated labels for every {@link models_QualificationLevel}. */
export const getQualificationLevelLabels = (
  i18n: I18n,
): Record<string, string> => ({
  [models_QualificationLevel.QualificationQualified]: i18n._(t({ id: 'employees.qualificationLevel.options.qualified', message: 'Qualified' })),
  [models_QualificationLevel.QualificationRecognitionPending]: i18n._(t({ id: 'employees.qualificationLevel.options.recognitionPending', message: 'Recognition Pending' })),
  [models_QualificationLevel.QualificationNotQualified]: i18n._(t({ id: 'employees.qualificationLevel.options.notQualified', message: 'Not Qualified' })),
});
